import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    CcReviewComplianceHistory,
    CcReviewComplianceHistoryCreate,
    CcReviewComplianceHistoryVersion,
    CcReviewComplianceTempAnswer,
    MtDriveVersion,
} from "server/models"

import {
    APPROVED,
    OPEN,
    VERIFICATION,
    WORKFLOW_RERUN,
    VIEW,
} from "const"
import { CcStateSettingsType, hasProjectAccess } from "client/utils"
import {
    PRM_CC_DRAFTS,
    StoreType,
    setCcReviewComplianceReloadAnswer,
    setCcReviewComplianceTempAnswerHistory,
    initReviewComplianceHistory,
} from "client/store"
import { PageForm } from "client/components"

import Form from "./form"

const Answer = () => {
    const version = useSelector((state: StoreType) => state.ccReviewDocument.activeVersion)

    const setList = useSelector((state: StoreType) => state.ccReviewCompliance.setList)
    const revisionActiveList = useSelector((state: StoreType) => state.ccReviewCompliance.revisionActiveList)
    const itemDetails = useSelector((state: StoreType) => state.ccReviewCompliance.itemDetails)
    const itemState = useSelector((state: StoreType) => state.ccReviewCompliance.itemState)
    const itemHistory = useSelector((state: StoreType) => state.ccReviewCompliance.itemHistory)
    const tempAnswerByUser = useSelector((state: StoreType) => state.ccReviewCompliance.tempAnswers)
    const tempAnswerVersionsByUser = useSelector((state: StoreType) => state.ccReviewCompliance.tempAnswerVersions)
    const reloadAnswer = useSelector((state: StoreType) => state.ccReviewCompliance.reloadAnswer)

    const levelList = useSelector((state: StoreType) => state.ccApprovalLevel.levelList)
    const moduleLevelList = useSelector((state: StoreType) => state.ccApprovalLevel.moduleLevelList)
    const reviewLevelIds = moduleLevelList[2] || []
    const formalLevelIds = moduleLevelList[0] || []
    const set = setList[itemDetails.librarySetId]
    const levelIds = set && set.formal ? formalLevelIds : reviewLevelIds

    const groupUserList = useSelector((state: StoreType) => state.projectUserGroup.groupUserList)

    const moduleResponseList = useSelector((state: StoreType) => state.ccResponse.moduleResponseList)
    const responseIds = moduleResponseList[2]
    const responsibleResponseIds = moduleResponseList[0]

    const userData = useSelector((state: StoreType) => state.system.userData)
    const dispatch = useDispatch()

    const [newHistoryCreate, setNewHistoryCreate] = useState<CcReviewComplianceHistoryCreate>(null)
    const [newHistoryVersions, setNewHistoryVersions] = useState<MtDriveVersion[]>([])

    const viewDraftsAccess = hasProjectAccess(PRM_CC_DRAFTS, VIEW)

    useEffect(() => {
        const { userId } = userData
        const { levelId, verifierId } = itemState || {}
        const settings: CcStateSettingsType = itemState && itemState.settings ? JSON.parse(itemState.settings) : {}
        const revision = (revisionActiveList[version.id] && revisionActiveList[version.id][itemDetails.librarySetId]) || null
        const isFirstLevel = levelId === levelIds[0]

        let lastHistory: CcReviewComplianceHistory = null
        let newHistory: CcReviewComplianceHistoryCreate = null
        let selectedLevelId: number = null
        let selectedUserId: number = null
        let userIds: readonly number[] = []
        let groupId: number = null
        let tempAnswer: CcReviewComplianceTempAnswer = null
        let tempAnswerHistory: CcReviewComplianceHistory = null

        // get the last history row made by user, not by 'system'
        for (let i = itemHistory.length - 1; i >= 0; i--) {
            const history = itemHistory[i]
            if (history.workflow === WORKFLOW_RERUN) {
                break
            }
            if (history.systemMessage === false) {
                lastHistory = history
                break
            }
        }

        let nextHistoryRow = true
        if (lastHistory && lastHistory.nextStatus === APPROVED) nextHistoryRow = false
        if (nextHistoryRow) {
            if (levelId && verifierId === userId) {
                // define users on assigned approval level
                groupId = levelList[levelId] ? levelList[levelId].groupId : null
                userIds = (groupUserList && groupUserList[groupId]) || []
                tempAnswer = tempAnswerByUser[userId] || null

                // if current user is assigned to approval level
                if (userIds.includes(userId)) {
                    // define next approval level and users
                    for (let i = 0; i < levelIds.length; i++) {
                        if (levelIds[i] === levelId && levelIds[i + 1]) {
                            selectedLevelId = levelIds[i + 1]
                            break
                        }
                    }
                    groupId = levelList[selectedLevelId] ? levelList[selectedLevelId].groupId : null
                    userIds = (groupUserList && groupUserList[groupId]) || []

                    if (settings[selectedLevelId]) {
                        selectedUserId = settings[selectedLevelId]
                        if (!userIds.includes(selectedUserId)) selectedUserId = null
                    }

                    newHistory = {
                        itemId: itemDetails.id,
                        reviewComplianceRevisionId: revision ? revision.id : null,
                        parentId: isFirstLevel ? null : (lastHistory && lastHistory.nextLevelId ? lastHistory.id : null),
                        prevLevelId: levelId,
                        prevUserId: userId,
                        nextLevelId: selectedLevelId,
                        nextUserId: tempAnswer ? tempAnswer.nextUserId : selectedUserId,
                        responseId: tempAnswer ? tempAnswer.responseId : (
                            isFirstLevel
                                ? (itemDetails.answerType === "yes_no" && responsibleResponseIds && responsibleResponseIds[0]) || null
                                : (responseIds && responseIds[0]) || null
                        ),
                        answer: tempAnswer ? tempAnswer.answer : "",
                        remark: tempAnswer ? tempAnswer.remark : "",
                        prevStatus: isFirstLevel ? OPEN : VERIFICATION,
                        nextStatus: VERIFICATION,
                        workflow: null,
                        systemMessage: false,
                        editorId: null,
                    }
                }
            }

            // add tempAnswer to history
            if (
                levelId && verifierId
                && verifierId !== userId
                && tempAnswerByUser[verifierId]
                && viewDraftsAccess
            ) {
                tempAnswer = tempAnswerByUser[verifierId]
                // define next approval level
                for (let i = 0; i < levelIds.length; i++) {
                    if (levelIds[i] === levelId && levelIds[i + 1]) {
                        selectedLevelId = levelIds[i + 1]
                        break
                    }
                }

                const tempAnswerHistoryVersions: CcReviewComplianceHistoryVersion[] = []
                if (tempAnswerVersionsByUser && tempAnswerVersionsByUser[verifierId]) {
                    tempAnswerVersionsByUser[verifierId].forEach((row) => {
                        if (row.version) {
                            tempAnswerHistoryVersions.push({
                                reviewComplianceHistoryId: -1,
                                versionId: row.version.id,
                                version: row.version,
                            })
                        }
                    })
                }

                tempAnswerHistory = {
                    ...initReviewComplianceHistory,
                    id: -1,
                    parentId: isFirstLevel ? null : (lastHistory && lastHistory.nextLevelId ? lastHistory.id : null),
                    prevLevelId: levelId,
                    prevUserId: verifierId,
                    nextLevelId: selectedLevelId,
                    nextUserId: tempAnswer.nextUserId || null,
                    responseId: tempAnswer.responseId || null,
                    answer: tempAnswer.answer || "",
                    remark: tempAnswer.remark || "",
                    prevStatus: isFirstLevel ? OPEN : VERIFICATION,
                    nextStatus: VERIFICATION,
                    systemMessage: false,

                    versions: tempAnswerHistoryVersions,
                }
            }
        }

        const versions: MtDriveVersion[] = []
        if (tempAnswerVersionsByUser && tempAnswerVersionsByUser[userId]) {
            tempAnswerVersionsByUser[userId].forEach((row) => {
                if (row.version) versions.push(row.version)
            })
        }

        setNewHistoryCreate(newHistory)
        setNewHistoryVersions(versions)
        dispatch(setCcReviewComplianceTempAnswerHistory(tempAnswerHistory))

        dispatch(setCcReviewComplianceReloadAnswer(false))
        setTimeout(() => {
            dispatch(setCcReviewComplianceReloadAnswer(true))
        }, 0)
    }, [
        itemDetails,
        itemState,
        itemHistory,
        revisionActiveList,
        tempAnswerByUser,
        tempAnswerVersionsByUser,
        levelList,
        moduleLevelList,
        levelIds,
        responseIds,
        responsibleResponseIds,
    ])

    if (!newHistoryCreate) return null

    return (
        <PageForm noPaddingTop>
            {
                !reloadAnswer
                && <Form history={newHistoryCreate} historyVersions={newHistoryVersions} />
            }
            {
                reloadAnswer
                && <Form history={newHistoryCreate} historyVersions={newHistoryVersions} />
            }
        </PageForm>
    )
}
export default Answer
